import { AsyncPipe, SlicePipe } from '@angular/common';
import { AfterContentInit, Component, ElementRef, OnDestroy, OnInit, Signal, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { map, skip, switchMap, take } from 'rxjs/operators';
import { Banner, BannerType, Category, Link } from 'src/app/models';
import { HomeType, ProductSection } from 'src/app/models/home';
import { AuthService } from 'src/app/services/auth';
import { BannerService } from 'src/app/services/banner.service';
import { CategoryService } from 'src/app/services/category/category.service';
import { ContentService } from 'src/app/services/content/content.service';
import { KioskService } from 'src/app/services/kiosk/kiosk.service';
import { ProductQuery, ProductService } from 'src/app/services/product';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { TextButtonComponent } from 'src/app/shared/components';
import { HeroBannerComponent } from 'src/app/shared/components/hero-banner/hero-banner.component';
import { ProductCardComponent } from 'src/app/shared/components/product/card';
import { ContentSection } from 'src/app/shared/layout/content-section';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-home-meny',
  templateUrl: './home-meny.component.html',
  styleUrls: ['./home-meny.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [HeroBannerComponent, RouterLink, AsyncPipe, SlicePipe, ProductCardComponent, TextButtonComponent],
})
export class HomeMenyComponent extends ContentSection implements OnInit, AfterContentInit, OnDestroy {
  private categoryService = inject(CategoryService);
  private bannerService = inject(BannerService);
  private productService = inject(ProductService);
  private productQuery = inject(ProductQuery);
  private authService = inject(AuthService);
  private themeService = inject(ThemeService);
  private kioskService = inject(KioskService);
  private elRef = inject(ElementRef);

  contentUrl = environment.cmsOrigin;
  productsUrl = environment.urls.products;

  private subscription: Subscription;

  heroBanners: Signal<Banner[]>;
  categories$: Observable<Category[]>;
  isKiosk: boolean;
  isEventShop: boolean;

  title: string;
  maxCategories: number;
  seeAll: string;
  categoriesTitle: string;

  leftBanner: Banner;
  rightBanner: Banner;

  primarySection: ProductSection;

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, HomeType.meny, { meta: true });

    this.subscription = new Subscription();

    this.title = this.content.get('title') || 'Populært lige nu';
    this.maxCategories = this.content.get('maxCategories') || 4;
    this.seeAll = this.content.get('seeAllCategoriesText') || 'Se alle';
    this.categoriesTitle = this.content.get('categoriesTitle') || 'Kategorier';

    this.heroBanners = toSignal(this.bannerService.getHeroBanners(this.contentService.getMerchantId()));
  }

  ngOnInit() {
    this.isKiosk = this.kioskService.isKiosk;
    this.isEventShop = this.contentService.isEventStore;

    this.subscription.add(
      this.bannerService.getBanners(this.contentService.getMerchantId()).subscribe((banners) => {
        this.leftBanner = banners.find((banner) => banner?.type === BannerType.Left);
        this.rightBanner = banners.find((banner) => banner?.type === BannerType.Right);
      }),
    );

    this.categories$ = this.categoryService.getAll().pipe(
      map((forestCategories) => {
        const categories = forestCategories.trees.map((t) => t.root.data).filter((category) => !category.hidden);
        for (const category of categories) {
          if (category.subCategories?.length) categories.push(...this.getAllChildCategories(category.subCategories));
        }
        return categories;
      }),
    );

    this.initFrontPageProducts();

    this.subscription.add(
      this.authService.member.pipe(skip(1)).subscribe(() => {
        this.initFrontPageProducts();
      }),
    );
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  initFrontPageProducts() {
    this.categoryService
      .getFrontpage()
      .pipe(take(1))
      .subscribe(async (frontpage) => {
        const categories = [...frontpage];
        const primaryCategory = categories.shift();
        if (primaryCategory) {
          await this.productService.getFrontpageProducts(primaryCategory.frontPageProductsCount, primaryCategory.id);
          const link: Link = {
            url: `${environment.urls.products}`,
            params: null,
            name: this.content.get('seeAllLocalOffers'), // TODO: how to name this?
          };
          if (!primaryCategory.hidden) {
            link.url = `${environment.urls.products}/${primaryCategory.categoryUrl}`;
          }

          const productSection: ProductSection = {
            title: primaryCategory.name,
            products: this.productQuery.selectFrontpageProducts$.pipe(switchMap((ids) => this.productQuery.selectMany(ids[0]))),
            link,
          };

          this.primarySection = productSection;
        }
      });
  }

  getCategoryUrl(categoryId?: number): string {
    return this.categoryService.buildUrl(categoryId);
  }

  sameSite(link: string): string {
    return this.contentService.sameSite({
      type: 'link',
      titleText: null,
      targetUrl: link,
      new: null,
    })?.targetUrl;
  }

  private getAllChildCategories(categories: Category[]): Category[] {
    const result = [];
    for (const category of categories) {
      if (category.hidden) continue;

      result.push(category);
      if (category.subCategories?.length) result.push(...this.getAllChildCategories(category.subCategories));
    }

    return result;
  }
}
